import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { btn, TextField, ErrorSummary, ErrorBox, DevLink } from "../components/kit";

const emailOk = (e) => /^\S+@\S+\.\S+$/.test(e);

function AuthCard({ title, sub, children }) {
  return (
    <div className="container-x py-12 md:py-16">
      <div className="mx-auto max-w-lg card p-6 md:p-10">
        <div className="mb-7 flex items-center gap-3" data-testid="auth-brand-lockup">
          <img
            src="/openpath-logo.png"
            alt="OpenPath"
            className="h-11 w-11 shrink-0 object-contain"
          />
          <div>
            <p className="text-lg font-extrabold text-navy">OpenPath</p>
            <p className="text-xs font-semibold text-sub">Every Ability. More Possibilities.</p>
          </div>
        </div>
        <h1 className="text-3xl font-extrabold">{title}</h1>
        {sub && <p className="mt-2 text-sub">{sub}</p>}
        <div className="mt-8">{children}</div>
      </div>
    </div>
  );
}

function PasswordField({ id, label, autoComplete, value, onChange, error, hint }) {
  const [show, setShow] = useState(false);
  const toggle = (
    <button type="button" onClick={() => setShow(!show)} className="grid h-11 w-11 place-items-center rounded-md text-sub hover:text-navy" aria-label={show ? "Hide password" : "Show password"} aria-pressed={show} data-testid={`${id}-toggle-visibility`}>
      {show ? <EyeOff className="h-5 w-5" aria-hidden="true" /> : <Eye className="h-5 w-5" aria-hidden="true" />}
    </button>
  );
  return <TextField id={id} label={label} required type={show ? "text" : "password"} autoComplete={autoComplete} value={value} onChange={onChange} error={error} hint={hint} adornment={toggle} />;
}

const MODES = [
  ["candidate", "I'm a candidate", "Find jobs, freelance work and opportunities"],
  ["employer", "I'm an employer", "Post jobs and manage applicants"],
];

function ModeToggle({ value, onChange, idPrefix }) {
  return (
    <div role="radiogroup" aria-label="What are you here to do?" className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid={`${idPrefix}-mode-toggle`}>
      {MODES.map(([v, l, h]) => {
        const active = value === v;
        return (
          <button key={v} type="button" role="radio" aria-checked={active} onClick={() => onChange(v)} data-testid={`${idPrefix}-mode-${v}`}
            className={`min-h-[64px] rounded-xl border-2 px-4 py-3 text-left transition-colors ${active ? "border-blue bg-mist text-navy" : "border-line bg-white text-ink hover:border-field"}`}>
            <span className="block font-bold">{l}</span>
            <span className="block text-sm text-sub">{h}</span>
          </button>
        );
      })}
    </div>
  );
}

function GoogleButton() {
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <>
      <div className="my-6 flex items-center gap-3 text-sub" aria-hidden="true"><span className="h-px flex-1 bg-line" />or<span className="h-px flex-1 bg-line" /></div>
      <button type="button" onClick={go} className={`${btn("secondary")} w-full`} data-testid="google-signin-btn">
        <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true"><path fill="#4285F4" d="M22.6 12.2c0-.8-.1-1.5-.2-2.2H12v4.2h5.9a5 5 0 0 1-2.2 3.3v2.7h3.6c2.1-1.9 3.3-4.8 3.3-8z"/><path fill="#34A853" d="M12 23c3 0 5.5-1 7.3-2.7l-3.6-2.7c-1 .7-2.2 1.1-3.7 1.1-2.9 0-5.3-1.9-6.2-4.5H2.1v2.8A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.8 14.2a6.6 6.6 0 0 1 0-4.3V7H2.1a11 11 0 0 0 0 10l3.7-2.8z"/><path fill="#EA4335" d="M12 5.4c1.6 0 3 .6 4.2 1.6l3.1-3.1A11 11 0 0 0 2.1 7l3.7 2.9C6.7 7.3 9.1 5.4 12 5.4z"/></svg>
        Continue with Google
      </button>
    </>
  );
}

export function SignIn() {
  const [sp] = useSearchParams();
  const { setUser, user } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [mode, setMode] = useState("candidate");
  const [errors, setErrors] = useState({});
  const [serverErr, setServerErr] = useState(sp.get("error") || "");
  const [busy, setBusy] = useState(false);
  const dest = (u) => {
    if (!u.onboarded) return "/onboarding";
    const wantsEmployer = mode === "employer" && (u.roles || []).includes("employer");
    return sp.get("next") || (wantsEmployer ? "/employer" : "/dashboard");
  };
  useEffect(() => { if (user) nav(dest(user), { replace: true }); });
  const submit = async (e) => {
    e.preventDefault();
    const er = {};
    if (!emailOk(f.email)) er["signin-email"] = "Enter a valid email address, like name@example.com.";
    if (!f.password) er["signin-password"] = "Enter your password.";
    setErrors(er); setServerErr("");
    if (Object.keys(er).length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", f);
      toast.success(`Welcome back, ${data.user.name}`);
      setUser(data.user);
    } catch (e2) { setServerErr(errMsg(e2)); setBusy(false); }
  };
  return (
    <AuthCard title="Sign in" sub="Welcome back to OpenPath.">
      <form onSubmit={submit} noValidate className="space-y-5" data-testid="signin-form">
        <ModeToggle value={mode} onChange={setMode} idPrefix="signin" />
        <ErrorSummary errors={errors} />
        {serverErr && <ErrorBox message={serverErr} />}
        <TextField id="signin-email" label="Email address" type="email" autoComplete="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} error={errors["signin-email"]} />
        <PasswordField id="signin-password" label="Password" autoComplete="current-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} error={errors["signin-password"]} />
        <div className="flex justify-end"><Link to="/forgot-password" className="link min-h-[44px] inline-flex items-center" data-testid="forgot-password-link">Forgot your password?</Link></div>
        <button className={`${btn()} w-full`} disabled={busy} data-testid="signin-submit-btn">{busy ? "Signing in…" : "Sign in"}</button>
      </form>
      <GoogleButton />
      <p className="mt-6 text-center">
        New to OpenPath? <Link to="/signup" className="link font-semibold" data-testid="goto-signup-link">
          Create an account
        </Link>
      </p>
    </AuthCard>
  );
}

export function SignUp() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [mode, setMode] = useState("candidate");
  const [errors, setErrors] = useState({});
  const [serverErr, setServerErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const er = {};
    if (f.name.trim().length < 2) er["signup-name"] = "Enter your full name (at least 2 characters).";
    if (!emailOk(f.email)) er["signup-email"] = "Enter a valid email address, like name@example.com.";
    if (f.password.length < 8) er["signup-password"] = "Use at least 8 characters for your password.";
    setErrors(er); setServerErr("");
    if (Object.keys(er).length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", { ...f, account_type: mode });
      setUser(data.user);
      if (data.dev_link) sessionStorage.setItem("aptiport-dev-verify", data.dev_link);
      toast.success(data.email_sent ? "Account created. Check your email to confirm your address." : "Account created. Email delivery is unavailable right now.");
      nav("/onboarding");
    } catch (e2) { setServerErr(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <AuthCard title="Create your account" sub="One account for jobs, freelancing, government opportunities and the community.">
      <form onSubmit={submit} noValidate className="space-y-5" data-testid="signup-form">
        <ModeToggle value={mode} onChange={setMode} idPrefix="signup" />
        <ErrorSummary errors={errors} />
        {serverErr && <ErrorBox message={serverErr} />}
        <TextField id="signup-name" label="Full name" autoComplete="name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} error={errors["signup-name"]} />
        <TextField id="signup-email" label="Email address" type="email" autoComplete="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} error={errors["signup-email"]} />
        <PasswordField id="signup-password" label="Password" autoComplete="new-password" hint="At least 8 characters. Password managers and pasting are supported." value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} error={errors["signup-password"]} />
        <p className="text-sm text-sub">By creating an account you agree to the <Link to="/terms" className="link">Terms</Link> and <Link to="/privacy" className="link">Privacy Policy</Link>. You will never be asked to disclose a disability.</p>
        <button className={`${btn()} w-full`} disabled={busy} data-testid="signup-submit-btn">{busy ? "Creating account…" : "Create account"}</button>
      </form>
      <GoogleButton />
      <p className="mt-6 text-center">Already have an account? <Link to="/signin" className="link font-semibold">Sign in</Link></p>
    </AuthCard>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [err, setErr] = useState("");
  const [res, setRes] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    if (!emailOk(email)) { setErr("Enter a valid email address, like name@example.com."); return; }
    setErr("");
    try { const { data } = await api.post("/auth/forgot-password", { email }); setRes(data); } catch (e2) { setErr(errMsg(e2)); }
  };
  return (
    <AuthCard title="Forgot your password?" sub="Enter your email and we'll send you a link to reset it.">
      {res ? (
        <div className="space-y-4" role="status" data-testid="forgot-success"><p className="flex gap-2 font-medium text-green"><CheckCircle2 className="h-5 w-5 shrink-0" aria-hidden="true" />{res.message}</p><DevLink link={res.dev_link} /><Link to="/signin" className="link">Back to sign in</Link></div>
      ) : (
        <form onSubmit={submit} noValidate className="space-y-5" data-testid="forgot-form">
          <TextField id="forgot-email" label="Email address" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} error={err} />
          <button className={`${btn()} w-full`} data-testid="forgot-submit-btn">Send reset link</button>
        </form>
      )}
    </AuthCard>
  );
}

export function ResetPassword() {
  const [sp] = useSearchParams();
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [done, setDone] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { setErr("Use at least 8 characters for your password."); return; }
    try { const { data } = await api.post("/auth/reset-password", { token: sp.get("token") || "", password: pw }); setDone(data.message); } catch (e2) { setErr(errMsg(e2)); }
  };
  return (
    <AuthCard title="Choose a new password">
      {done ? <div role="status" className="space-y-4" data-testid="reset-success"><p className="font-medium text-green">{done}</p><Link to="/signin" className={btn()}>Sign in</Link></div> : (
        <form onSubmit={submit} noValidate className="space-y-5" data-testid="reset-form">
          <PasswordField id="reset-password" label="New password" autoComplete="new-password" hint="At least 8 characters." value={pw} onChange={(e) => setPw(e.target.value)} error={err} />
          <button className={`${btn()} w-full`} data-testid="reset-submit-btn">Update password</button>
        </form>
      )}
    </AuthCard>
  );
}

export function VerifyEmail() {
  const [sp] = useSearchParams();
  const { reload } = useAuth();
  const [state, setState] = useState({ loading: true });
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    api.post("/auth/verify-email", { token: sp.get("token") || "" })
      .then(({ data }) => { setState({ ok: data.message }); sessionStorage.removeItem("aptiport-dev-verify"); reload(); })
      .catch((e) => setState({ err: errMsg(e) }));
  }, [sp, reload]);
  return (
    <AuthCard title="Confirm your email">
      <div role="status" data-testid="verify-email-status">
        {state.loading && <p>Confirming…</p>}
        {state.ok && <><p className="font-medium text-green">{state.ok}</p><Link to="/dashboard" className={`${btn()} mt-4`}>Go to dashboard</Link></>}
        {state.err && <ErrorBox message={state.err} />}
      </div>
    </AuthCard>
  );
}
